import { type FC, type KeyboardEventHandler, useId } from "react";
import { Select } from "antd";
import { ReactComponent as ArrowDownIcon } from "../../../../assets/images/keyboard-arrow-down.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/images/delete.svg";
import NumberInput from "../NumberInput";

export interface StdRateOption {
  label: string;
  value: string;
}

export interface StdRateFieldsProps {
  productLabel: string;
  productPlaceholder: string;
  productOptions: StdRateOption[];
  productValue?: string;
  onProductChange?: (value: string) => void;
  productError?: string;
  rateLabel: string;
  rateValue?: number | null;
  onRateChange?: (value: number | null) => void;
  rateError?: string;
  ratePrecision?: number;
  rateStep?: number;
  onRateKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  deleteLabel?: string;
  onDelete?: () => void;
}

const StdRateFields: FC<StdRateFieldsProps> = ({
  productLabel,
  productPlaceholder,
  productOptions,
  productValue,
  onProductChange,
  productError,
  rateLabel,
  rateValue,
  onRateChange,
  rateError,
  ratePrecision,
  rateStep,
  onRateKeyDown,
  deleteLabel,
  onDelete,
}) => {
  const id = useId();
  const productId = `${id}-product`;
  const productErrorId = `${id}-product-error`;
  const rateId = `${id}-rate`;
  const rateErrorId = `${id}-rate-error`;

  return (
    <div className="rd-std-seed-rate__fields" data-testid="std-rate-fields">
      <div className="rd-std-seed-rate__field rd-std-seed-rate__field--product">
        <label className="rd-std-seed-rate__label" htmlFor={productId}>
          {productLabel}
        </label>
        <Select
          id={productId}
          className="rd-std-seed-rate__product-select"
          placeholder={productPlaceholder}
          value={productValue}
          options={productOptions}
          onChange={onProductChange}
          status={productError ? "error" : undefined}
          aria-invalid={Boolean(productError)}
          aria-describedby={productError ? productErrorId : undefined}
          suffixIcon={
            <span className="rd-std-seed-rate__select-arrow">
              <ArrowDownIcon aria-hidden />
            </span>
          }
        />
        {productError && (
          <span
            id={productErrorId}
            className="rd-std-seed-rate__error"
            role="alert"
          >
            {productError}
          </span>
        )}
      </div>
      <div className="rd-std-seed-rate__field rd-std-seed-rate__field--seed-rate">
        <label className="rd-std-seed-rate__label" htmlFor={rateId}>
          {rateLabel}
        </label>
        <NumberInput
          id={rateId}
          className="rd-std-seed-rate__rate-input"
          min={0}
          precision={ratePrecision}
          step={rateStep}
          onKeyDown={onRateKeyDown}
          value={rateValue}
          status={rateError ? "error" : undefined}
          aria-invalid={Boolean(rateError)}
          aria-describedby={rateError ? rateErrorId : undefined}
          onChange={onRateChange}
        />
        {rateError && (
          <span
            id={rateErrorId}
            className="rd-std-seed-rate__error"
            role="alert"
          >
            {rateError}
          </span>
        )}
      </div>
      {onDelete && (
        <button
          type="button"
          className="rd-std-seed-rate__delete-btn"
          aria-label={deleteLabel}
          onClick={onDelete}
        >
          <DeleteIcon aria-hidden />
        </button>
      )}
    </div>
  );
};

export default StdRateFields;
